import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "@fmc-digital/i18n";
import { Button, cn, ErrorBanner } from "@fmc-digital/ui";
import { NpsRatingScale } from "@profile/components/molecules/NpsRatingScale/NpsRatingScale";
import { FeedbackIcon } from "@profile/components/registration-wizard/icons/FeedbackIcon";
import { submitRegistrationFeedback } from "@profile/components/registration-wizard/feedback/submitRegistrationFeedback";
import type { RegistrationFeedbackMetadata } from "@profile/components/registration-wizard/feedback/registrationFeedback.types";
import { useRegistrationConfig } from "@profile/providers/RegistrationProvider";
import {
  getRegisterFeedbackExitNavigation,
  getRegisterFeedbackNavigationAfterRating,
  getRegisterFeedbackThankYouNavigation,
  type RegisterFeedbackLocationState,
} from "@profile/components/registration-wizard/feedback/register-feedback.navigation";
import {
  registrationFeedbackMaybeLaterStyles,
  registrationFeedbackNpsActionsStyles,
  registrationFeedbackNpsErrorBannerStyles,
  registrationFeedbackNpsErrorBannerWrapperStyles,
  registrationFeedbackNpsHeaderStyles,
  registrationFeedbackNpsQuestionStyles,
  registrationFeedbackNpsRootStyles,
  registrationFeedbackNpsScaleSectionStyles,
  registrationFeedbackNpsTitleStyles,
} from "./registration-feedback-nps.styles";

const EMPTY_FEEDBACK_METADATA: RegistrationFeedbackMetadata = {
  email: "",
  userUUID: "",
  loanNumber: "",
};

function isPassiveNpsRating(rating: number | null): boolean {
  return rating !== null && rating >= 7 && rating <= 8;
}

export interface RegistrationFeedbackNpsProps {
  username?: string;
  feedbackContext?: RegistrationFeedbackMetadata;
  feedbackState?: RegisterFeedbackLocationState;
}

export function RegistrationFeedbackNps({
  username,
  feedbackContext,
  feedbackState,
}: Readonly<RegistrationFeedbackNpsProps>) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const registrationConfig = useRegistrationConfig();
  const [rating, setRating] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const navigateAfterFeedback = () => {
    const { route, state } = getRegisterFeedbackExitNavigation(username);
    navigate(route, state ? { state } : undefined);
  };

  const handleMaybeLater = () => {
    navigateAfterFeedback();
  };

  const handleRatingChange = (value: number) => {
    setRating(value);
    setSubmitError(null);
  };

  const handleSendFeedback = async () => {
    if (rating === null || submittingRef.current) {
      return;
    }

    submittingRef.current = true;
    setLoading(true);
    setSubmitError(null);
    try {
      if (rating >= 7 && rating <= 8) {
        try {
          await submitRegistrationFeedback(feedbackContext ?? EMPTY_FEEDBACK_METADATA, rating, registrationConfig);
        } catch (error) {
          console.error("[Registration NPS] Feedback submission failed", error);
          setSubmitError(t("auth.registration.feedback.nps.submitError"));
          return;
        }
        const { route, state } = getRegisterFeedbackThankYouNavigation(feedbackState, rating);
        navigate(route, state ? { state } : undefined);
        return;
      }

      const { route, state } = getRegisterFeedbackNavigationAfterRating(rating, feedbackState);
      navigate(route, { state });
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  const showSendFeedbackLabel = rating === null || isPassiveNpsRating(rating);

  let buttonLabel: string;
  if (loading) {
    buttonLabel = t("auth.registration.feedback.nps.submitting");
  } else if (showSendFeedbackLabel) {
    buttonLabel = t("auth.registration.feedback.nps.sendFeedback");
  } else {
    buttonLabel = t("auth.registration.feedback.nps.next");
  }

  return (
    <div className={cn(registrationFeedbackNpsRootStyles())} data-testid="registration-feedback-nps">
      <div className={cn(registrationFeedbackNpsHeaderStyles())}>
        <FeedbackIcon />
        <h1 className={cn(registrationFeedbackNpsTitleStyles())}>
          {t("auth.registration.feedback.nps.title")}
        </h1>
        <p className={cn(registrationFeedbackNpsQuestionStyles())}>
          {t("auth.registration.feedback.nps.question")}
        </p>
      </div>

      <div className={cn(registrationFeedbackNpsScaleSectionStyles())}>
        <NpsRatingScale
          value={rating}
          onChange={handleRatingChange}
          ariaLabel={t("auth.registration.feedback.nps.scaleAriaLabel")}
          lowLabel={t("auth.registration.feedback.nps.lowLabel")}
          highLabel={t("auth.registration.feedback.nps.highLabel")}
        />
      </div>

      <div className={cn(registrationFeedbackNpsActionsStyles())}>
        <Button
          variant="filled"
          fullWidth
          onClick={handleSendFeedback}
          disabled={rating === null || loading}
          data-testid="feedback-nps-send-button"
          aria-label={
            showSendFeedbackLabel
              ? t("auth.registration.feedback.nps.sendFeedbackAriaLabel")
              : t("auth.registration.feedback.nps.nextAriaLabel")
          }
        >
          {buttonLabel}
        </Button>
        <button
          type="button"
          className={cn(registrationFeedbackMaybeLaterStyles())}
          onClick={handleMaybeLater}
          data-testid="feedback-nps-maybe-later-button"
          aria-label={t("auth.registration.feedback.nps.maybeLaterAriaLabel")}
        >
          {t("auth.registration.feedback.nps.maybeLater")}
        </button>
      </div>

      {submitError ? (
        <div className={cn(registrationFeedbackNpsErrorBannerWrapperStyles())}>
          <ErrorBanner
            message={submitError}
            className={cn(registrationFeedbackNpsErrorBannerStyles())}
            testID="feedback-nps-error-banner"
          />
        </div>
      ) : null}
    </div>
  );
}
